
import TextField from '@mui/material/TextField'
import useConditionalEffect from 'hooks/useConditionalEffect'
import { useEffectUpdateOnly } from 'hooks/useEffectUpdateOnly'
import { Article, ARTICLE_TYPE, ContentItem } from 'interfaces'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { from, Observable, Subject } from 'rxjs'
import { debounceTime, filter, map, switchMap } from 'rxjs/operators'
import { getLinkDetails, setPinDescription, setPinDestinationUrl, setPinTitle } from 'services/compose'
import {
  composerContentItemSelector,
  composerLinkSelector,
  composerPinterestPostStatusSelector,
  composerPostAsPhotoSelector,
  composerPostSelector,
  ComposerPostStatus,
  composerResetKeySelector
} from 'services/compose/selectors'
import { PINTEREST_DESCRIPTION_MAX_LENGTH, PINTEREST_TITLE_MAX_LENGTH } from 'shared/constants'
import { ComposerPinterestPost, LinkDetails } from 'shared/types'
import { debounce } from 'shared/utils'
import StoreState, { StoreThunkDispatch } from 'store/state'
import { extractUrlsFromText } from 'utils/composer'
import styles from './PinterestPostStatus.pcss'
import { BRAND_PINTEREST } from 'shared'

const INPUT_DEBOUNCE_TIME = 250
interface PinterestPostStatusConnectedProps {
  post: ComposerPinterestPost
  linkDetails?: LinkDetails
  resetKey: number
  status: ComposerPostStatus
  postAsPhoto: boolean
  content?: ContentItem
  setTitle: (title: string, auto: boolean) => void
  setDescription: (text: string, auto: boolean) => void
  setDestinationUrl: (url: string) => void
  fetchLinkDetails: (url: string) => Observable<{ link: LinkDetails }>
}

type PinterestPostStatusProps = PinterestPostStatusConnectedProps

export function PinterestPostStatus(props: PinterestPostStatusProps) {
  const intl = useIntl()
  const { linkDetails, setDestinationUrl, fetchLinkDetails } = props
  const [title, setTitle] = React.useState(props.post.title)
  const [description, setDescription] = React.useState(props.post.description || '')
  const [destinationUrl, setDestinationUrlLocal] = React.useState(props.post.destinationUrl)
  const saveTitle = debounce(props.setTitle, INPUT_DEBOUNCE_TIME)
  const saveDescription = debounce(props.setDescription, INPUT_DEBOUNCE_TIME)

  const titleRef = React.useRef<string>(undefined)
  const descriptionRef = React.useRef<string>(undefined)

  React.useEffect(() => {
    titleRef.current = title
  }, [title])

  React.useEffect(() => {
    descriptionRef.current = description
  }, [description])

  useConditionalEffect(([destUrl]) => {
    if (!props.postAsPhoto && linkDetails?.type === 'link') {
      const url = linkDetails.url
      if (!destUrl) {
        setDestinationUrlLocal(url || '')
        setDestinationUrl(url)
      }
    }
  }, [props.postAsPhoto, linkDetails], [destinationUrl])

  React.useEffect(() => {
    if (!props.post.status.touched) {
      setTitle(current => {
        if (current !== props.post.title) {
          return props.post.title || ''
        }
        return current
      })
    }
  }, [props.post.status.touched, props.post.title])

  React.useEffect(() => {
    if (!props.post.status.touched) {
      setDescription(current => {
        if (current !== props.post.description) {
          return props.post.description || ''
        }
        return current
      })
    }
  }, [props.post.status.touched, props.post.description])

  React.useEffect(() => {
    if (!props.post.status.touched) {
      setDestinationUrlLocal(current => {
        if (current !== props.post.destinationUrl) {
          return props.post.destinationUrl || ''
        }
        return current
      })
    }
  }, [props.post.status.touched, props.post.destinationUrl])

  const linkDetails$ = React.useRef<LinkDetails | undefined>(undefined)
  React.useEffect(() => {
    linkDetails$.current = linkDetails
  }, [linkDetails])

  const updateFields = (
    t: string,
    d: string,
    u: string,
    source: { title?: string; description?: string; url?: string }
  ) => {
    if (t.trim() === '') {
      updateTitle(source.title || '', true)
    }
    if (d.trim() === '' || d === source.url) {
      updateDescription(source.description || '', true)
    }
    if (u.trim() === '') {
      setDestinationUrlLocal(source.url || '')
      setDestinationUrl(source.url || '')
    }
  }

  useConditionalEffect(([t, d, u]) => {
    if (linkDetails?.type === 'link') {
      updateFields(t, d, u, linkDetails)
    } else if (props.content?.type === ARTICLE_TYPE) {
      updateFields(t, d, u, {
        title: (props.content as Article).title,
        description: (props.content as Article).description,
        url: (props.content as Article).sourceLink
      })
    }
  }, [linkDetails, props.content], [title, description, destinationUrl])

  useEffectUpdateOnly(() => {
    setTitle('')
    setDescription('')
    setDestinationUrlLocal('')
  }, [props.resetKey])

  const link$ = React.useRef<Subject<string>>(undefined)
  React.useEffect(() => {
    link$.current = new Subject()
    link$.current.pipe(
      debounceTime(INPUT_DEBOUNCE_TIME))
      .pipe(
        map((value: string) => {
          const url = extractUrlsFromText(value)[0]
          const lastFetchedUrl = linkDetails$.current?.url
          if (url && url !== lastFetchedUrl) {
            const validUrl = url.toLowerCase().startsWith('http') ? url : `http://${url}`
            return validUrl
          }
          return null
        }),
        filter(Boolean),
        switchMap(fetchLinkDetails)
      )

      .subscribe((response: any) => {
        const link = response.link
        if (link.title && !titleRef.current) {
          setTitle(link.title)
          saveTitle(link.title, true)
        }

        if (link.description && !descriptionRef.current) {
          setDescription(link.description)
          saveDescription(link.description, true)
        }
      })

    return () => {
      link$.current?.unsubscribe()
    }
  }, [fetchLinkDetails, saveTitle, saveDescription])

  const updateTitle = (text: string, auto: boolean) => {
    setTitle(text)
    saveTitle(text, auto)
  }

  const updateDescription = (text: string, auto: boolean) => {
    setDescription(text || '')
    saveDescription(text || '', auto)
  }

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    updateTitle(value, false)
  }

  const onDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    updateDescription(value, false)
  }

  const onLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    link$.current?.next(value)
    setDestinationUrl(value)
    setDestinationUrlLocal(value)
  }

  const titleRemainingChars = PINTEREST_TITLE_MAX_LENGTH - title.length
  // eslint-disable-next-line no-magic-numbers
  const titleHelper = titleRemainingChars < 51 ? (PINTEREST_TITLE_MAX_LENGTH - title.length).toString() : undefined
  const descriptionRemainingChars = PINTEREST_DESCRIPTION_MAX_LENGTH - description.length
  // eslint-disable-next-line no-magic-numbers
  const descriptionHelper = descriptionRemainingChars < 51 ? (PINTEREST_DESCRIPTION_MAX_LENGTH - description.length).toString() : undefined

  return (
    <div className={styles.container}>
      <TextField
        value={title}
        placeholder={intl.formatMessage({ id: 'composer.labels.pinterest-title-placeholder' })}
        classes={{ root: styles['title-box'] }}
        helperText={titleHelper}
        FormHelperTextProps={{ className: styles.helper }}
        name="Pin title"
        onChange={onTitleChange}
      />
      <TextField
        value={description}
        multiline
        placeholder={intl.formatMessage({ id: 'composer.labels.pinterest-text-placeholder' })}
        className={styles['description-box']}
        classes={{ root: styles['description-input'] }}
        FormHelperTextProps={{ className: styles.helper }}
        helperText={descriptionHelper}
        name="Pin description"
        onChange={onDescriptionChange}
      />
      <TextField
        value={destinationUrl}
        placeholder={intl.formatMessage({ id: 'composer.labels.pinterest-link-placeholder' })}
        className={styles['link-box']}
        classes={{ root: styles['link-input'] }}
        onChange={onLinkChange}
      />
    </div>
  )
}

function mapStateToProps(state: StoreState) {
  return {
    post: composerPostSelector(state, BRAND_PINTEREST),
    postAsPhoto: composerPostAsPhotoSelector(state),
    status: composerPinterestPostStatusSelector(state),
    content: composerContentItemSelector(state),
    linkDetails: composerLinkSelector(state),
    resetKey: composerResetKeySelector(state)
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    setTitle: (title: string, auto: boolean) => dispatch(setPinTitle({ title, auto })),
    setDescription: (text: string, auto: boolean) => dispatch(setPinDescription({ description: text, auto })),
    setDestinationUrl: (url: string) => dispatch(setPinDestinationUrl(url)),
    fetchLinkDetails: (url: string) => {
      return from(dispatch(getLinkDetails({ url, isPinterestDestinationUrl: true })).unwrap())
    }
  }
}

const ConnectedPinterestPostStatus = connect(mapStateToProps, mapDispatchToProps)(PinterestPostStatus)
export default ConnectedPinterestPostStatus
